import { Button } from '@datahub/design-system'
import type { PortalContext } from '../lib/types'
import { PortalPage } from './PortalPage'

interface LandingProps {
  context: PortalContext
  onStart: () => void
}

const VALUE_POINTS = [
  'Investors, acquirers, media and researchers use Stears to find and evaluate companies like yours.',
  'You control what is published: anything you mark confidential stays in our internal records only.',
  'It is free, and it supports a database built in and for Africa.',
]

/** Form/Auth two-panel pattern without the marketing screenshot (plan Section 12.5). */
export function Landing({ context, onStart }: LandingProps) {
  const resuming = (context.draft?.overallProgressPct ?? 0) > 0
  const firstTime = !context.submission

  return (
    <PortalPage>
      <div className="flex" style={{ minHeight: 'calc(100% - 44px)', flexWrap: 'wrap' }}>
        <div className="flex flex-col justify-center" style={{ flex: '1 1 380px', padding: 48, gap: 24 }}>
          <h1 className="font-jakarta font-bold text-white" style={{ fontSize: 20, lineHeight: '24px' }}>
            Keep {context.company.displayName}&rsquo;s profile accurate on Stears
          </h1>
          <p className="font-mulish text-text-description" style={{ fontSize: 12, lineHeight: '22px', maxWidth: 460 }}>
            Stears keeps a profile of {context.company.displayName} used by investors and researchers across African
            private markets. Review what we hold, correct anything that is wrong, and add what is missing.
          </p>
          <ul className="flex flex-col" style={{ gap: 12, listStyle: 'none', padding: 0, margin: 0, maxWidth: 460 }}>
            {VALUE_POINTS.map((point) => (
              <li key={point} className="flex" style={{ gap: 10 }}>
                <span className="flex-shrink-0" style={{ color: '#85BEFF', fontSize: 12, lineHeight: '22px' }}>✓</span>
                <span className="font-mulish text-white" style={{ fontSize: 12, lineHeight: '22px' }}>{point}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="flex flex-col justify-center" style={{ flex: '1 1 420px', padding: 48 }}>
          <div className="flex flex-col bg-background-main" style={{ border: '0.5px solid #4B5563', maxWidth: 460 }}>
            <div
              className="flex items-center bg-background-skeleton"
              style={{ height: 36, padding: '0 20px', borderBottom: '0.5px solid #4B5563' }}
            >
              <span className="font-mulish text-white" style={{ fontSize: 14, letterSpacing: '-0.14px' }}>
                {context.cycle ? `${context.cycle.periodLabel} update` : 'Your profile'}
              </span>
            </div>
            <div className="flex flex-col" style={{ padding: 20, gap: 16 }}>
              {[
                ['Company', context.company.displayName],
                ['Period', context.cycle?.periodLabel ?? '—'],
                ['Deadline', context.cycle?.deadlineLabel ?? '—'],
                ['Estimated time', firstTime ? 'About 30 minutes. You can stop and come back.' : 'About 10 minutes'],
                ['Your Stears contact', context.owner ? `${context.owner.name} (${context.owner.email})` : 'The Stears data team'],
                ...(resuming ? [['Progress', `${context.draft!.overallProgressPct}% complete`]] : []),
              ].map(([label, value]) => (
                <div key={label} className="flex" style={{ gap: 12 }}>
                  <span className="font-mulish text-text-support flex-shrink-0" style={{ fontSize: 12, width: '42%' }}>{label}</span>
                  <span className="font-mulish text-white" style={{ fontSize: 12, flex: 1 }}>{value}</span>
                </div>
              ))}

              {context.cycle?.customMessage && (
                <p className="font-mulish text-white" style={{ fontSize: 12, lineHeight: '22px', borderTop: '0.5px solid #4B5563', paddingTop: 16, whiteSpace: 'pre-line' }}>
                  {context.cycle.customMessage}
                </p>
              )}

              <div style={{ paddingTop: 8 }}>
                <Button variant="filled-grey" size="medium" onClick={onStart} className="w-full">
                  {resuming ? 'Continue where you left off' : 'Start'}
                </Button>
              </div>
              {resuming && context.draft && (
                <p className="font-mulish text-text-description" style={{ fontSize: 10 }}>
                  Last saved {new Date(context.draft.lastSavedAt).toLocaleString('en-GB')}
                </p>
              )}
              <p className="font-mulish text-text-description" style={{ fontSize: 10, lineHeight: '16px' }}>
                This link is unique to {context.company.displayName}. Several colleagues can use it at once. There is no
                password and no account to create.
              </p>
            </div>
          </div>
        </div>
      </div>
    </PortalPage>
  )
}
